<template>
  <div :class="{ 'reports-history-containerless': containerless }">
    <!-- Toolbar: refresh button + auto-polling indicator. -->
    <div
      class="d-flex align-items-center justify-content-end px-3 pb-2"
      v-if="!loading"
    >
      <small v-if="pollHandle" class="text-muted mr-2">
        Auto-refreshing every {{ Math.round(pollIntervalMs / 1000) }} seconds…
      </small>
      <button
        type="button"
        class="btn btn-sm btn-black"
        :disabled="loading"
        :title="pollHandle ? 'Click to refresh now' : 'Refresh'"
        @click="refresh"
      >
        <i
          class="fas fa-sync-alt"
          :class="{ 'fa-spin': pollHandle || refreshing }"
        ></i>
      </button>
    </div>

    <!-- Loading State -->
    <alert v-if="loading" class="light-shadow mx-4 mb-4" />

    <!-- Reports Table -->
    <div
      v-if="!loading"
      class="card overflow-auto light-shadow border border-lg-0"
    >
      <!-- Empty State -->
      <div class="p-3 noPrint" v-if="reports.length === 0">
        No reports generated yet.
      </div>

      <!-- Table -->
      <div class="overflow-auto border border-lg-0" v-if="reports.length > 0">
        <table class="table table-sm table-striped reportTable mb-0">
          <thead>
            <tr>
              <th rowspan="1" colspan="1">Report Type</th>
              <th rowspan="1" colspan="1">Format</th>
              <th rowspan="1" colspan="1">Created At</th>
              <th rowspan="1" colspan="1">Status</th>
              <th rowspan="1" colspan="1">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="report in reports"
              :key="report.id"
              :class="{ 'active-row': report.id === focusedReport }"
            >
              <td>
                {{ reportDisplayName(report) }} {{ report.id }}
                <span
                  v-if="
                    seenAtOpenIds.length > 0 &&
                    !seenAtOpenIds.includes(report.id) &&
                    (report.status === 'COMPLETED' ||
                      report.status === 'FAILED')
                  "
                  class="badge badge-pill badge-deep-success ml-1 align-middle"
                  >New</span
                >
              </td>
              <td>{{ (report.format || "").toUpperCase() }}</td>
              <td>{{ formatDate(report.created_at) }}</td>
              <td>
                <span
                  class="badge badge-pill font-weight-bold p-2"
                  :class="getStatusBadgeClass(report.status)"
                >
                  {{ formatStatus(report.status) }}
                </span>
              </td>
              <td class="d-flex align-items-center">
                <a
                  v-if="report.status === 'COMPLETED' && report.download_url"
                  :href="report.download_url"
                  target="_blank"
                  class="btn btn-sm btn-black mr-2"
                >
                  <i class="fas fa-download"></i> Download
                </a>
                <button
                  class="btn btn-sm btn-danger"
                  @click="confirmDelete(report)"
                  :disabled="report.status === 'PROCESSING'"
                  title="Delete report"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="p-3" v-if="pagination.totalPages > 1">
        <Pagination
          :current-page="pagination.currentPage"
          :total-pages="pagination.totalPages"
          :total="pagination.total"
          @page-changed="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination.vue";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

const swalWithBootstrapButtons = Swal.mixin({
  customClass: {
    confirmButton: "btn btn-black ml-4",
    cancelButton: "btn btn-danger",
  },
  buttonsStyling: false,
});

export default {
  components: {
    Pagination,
  },
  props: {
    reportType: {
      type: String,
      default: null,
    },
    perPage: {
      type: Number,
      default: null,
    },
    containerless: {
      type: Boolean,
      default: false,
    },
    focusedReport: {
      type: Number,
      default: 0,
    },
    seenAtOpenIds: {
      type: Array,
      default: () => [],
    },
    /**
     * When set, the component polls the API at this interval (ms) for as long
     * as at least one report is still PENDING or PROCESSING. Polling stops
     * automatically when all rows reach a terminal state, and on unmount.
     */
    pollIntervalMs: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      pollHandle: null,
      refreshing: false,
    };
  },
  async mounted() {
    await this.loadReports();
    this.maybeStartPolling();
  },
  beforeUnmount() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  },
  computed: {
    ...mapState({
      loading: (state) => state.reports.generatedReports.loading,
      allReports: (state) => state.reports.generatedReports.data,
      pagination: (state) => state.reports.generatedReports.pagination,
    }),
    reports() {
      return this.allReports;
    },
  },
  methods: {
    ...mapActions({
      fetchGeneratedReports: "reports/getGeneratedReports",
      deleteReport: "reports/deleteGeneratedReport",
    }),
    async loadReports(page = 1, silent = false) {
      const payload = { page, silent };
      if (this.perPage) payload.limit = this.perPage;
      if (this.reportType) payload.type = this.reportType;
      await this.fetchGeneratedReports(payload);
    },
    onPageChange(page) {
      this.loadReports(page);
    },
    async refresh() {
      this.refreshing = true;
      await this.loadReports(this.pagination.currentPage || 1, true);
      this.refreshing = false;
      this.maybeStartPolling();
    },
    hasInFlightReports() {
      return this.reports.some(
        (r) => r.status === "PENDING" || r.status === "PROCESSING"
      );
    },
    maybeStartPolling() {
      if (!this.pollIntervalMs || this.pollHandle) {
        return;
      }
      if (!this.hasInFlightReports()) {
        return;
      }
      this.pollHandle = setInterval(async () => {
        await this.loadReports(this.pagination.currentPage || 1, true);
        if (!this.hasInFlightReports()) {
          this.stopPolling();
        }
      }, this.pollIntervalMs);
    },
    stopPolling() {
      if (this.pollHandle) {
        clearInterval(this.pollHandle);
        this.pollHandle = null;
      }
    },
    async confirmDelete(report) {
      const result = await swalWithBootstrapButtons.fire({
        title: "Delete Report?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        const ok = await this.deleteReport(report.id);
        if (ok) {
          await this.loadReports(this.pagination.currentPage || 1, true);
        }
      }
    },
    getStatusBadgeClass(status) {
      const classMap = {
        PENDING: "badge-warning",
        PROCESSING: "badge-info",
        COMPLETED: "badge-deep-success",
        FAILED: "badge-danger",
      };
      return classMap[status] || "badge-secondary";
    },
    formatStatus(status) {
      const statusMap = {
        PENDING: "Pending",
        PROCESSING: "Processing",
        COMPLETED: "Completed",
        FAILED: "Failed",
      };
      return statusMap[status] || status;
    },
    formatReportType(type) {
      const typeMap = {
        billing: "Billing Report",
        payroll: "Payroll Report",
        group_session_billing: "Group Session Billing",
        group_session_payroll: "Group Session Payroll",
        group_session_attendance: "Group Session Attendance",
      };
      return typeMap[type] || type;
    },

    /**
     * HCAI billing reports share type="billing" and are distinguished only by
     * the hcaiFormat filter, so label them apart in the combined list.
     */
    reportDisplayName(report) {
      if (
        report.type === "billing" &&
        report.filters &&
        report.filters.hcaiFormat
      ) {
        return "HCAI Billing Report";
      }
      return this.formatReportType(report.type);
    },
    formatDate(date) {
      return dayjs(date).format("MMM D, YYYY h:mm A");
    },
  },
};
</script>

<style lang="scss" scoped>
.badge {
  font-size: 0.75rem;
}

.active-row {
  box-shadow: inset 0px 0px 9px 0px var(--theme-color);
  border-radius: 6px;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: inset 0px 0px 9px 2px var(--theme-color);
  }
  70% {
    box-shadow: inset 0px 0px 2px 1px var(--theme-color);
  }
  100% {
    box-shadow: inset 0px 0px 7px 1px var(--theme-color);
  }
}

/* Opt-in via :containerless prop: strip the card's border/shadow so
   the table flows directly on the parent's background (used in the drawer
   to avoid a visible "card ended here" edge in the middle of the panel). */
.reports-history-containerless > .card {
  border: 0 !important;
  box-shadow: none !important;
  background: transparent;
}
</style>
